import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Fingerprint, LogIn } from "lucide-react";
import { authApi } from "../lib/api";
import FinPipMark from "../components/FinPipMark";
import ThemeToggle from "../components/ThemeToggle";
import { useAuth } from "../lib/auth";
import { isPasskeySupported, loginPasskey } from "../lib/passkey";
import { useI18n } from "../lib/i18n";

type Mode = "sign-in" | "register";

export default function LoginPage() {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeyOk = isPasskeySupported();

  const { refresh, registrationEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = (location.state as { from?: { pathname?: string } })?.from?.pathname || "/";

  // Force sign-in mode if the server has locked down registration.
  if (!registrationEnabled && mode === "register") {
    setMode("sign-in");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "sign-in") {
        await authApi.login({ email, password });
      } else {
        await authApi.register({ email, password, name });
      }
      await refresh();
      navigate(next, { replace: true });
    } catch (err) {
      const e = err as { status?: number; body?: { seconds?: number }; message?: string };
      if (e.status === 429) {
        setError(t("login.throttled", { seconds: e.body?.seconds ?? 60 }));
      } else {
        setError(e.message || t("login.signInFailed"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onPasskey() {
    if (!email.trim()) {
      setError(t("login.passkeyEnterEmail"));
      return;
    }
    setError(null);
    setPasskeyBusy(true);
    try {
      await loginPasskey(email.trim());
      await refresh();
      navigate(next, { replace: true });
    } catch (err) {
      setError((err as Error).message || t("login.passkeyFailed"));
    } finally {
      setPasskeyBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="absolute top-4 end-4">
        <ThemeToggle compact />
      </div>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <FinPipMark className="w-10 h-10 text-[#0c34f4] dark:text-white" />
          <div>
            <p className="text-xl font-bold text-parchment tracking-tight">FinPip Journal</p>
            <p className="text-parchment-muted text-xs">{t("login.tagline")}</p>
          </div>
        </div>

        <section className="glass-gold p-6 sm:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-parchment">
              {mode === "sign-in" ? t("login.signIn") : t("login.createAccount")}
            </h1>
            <p className="mt-1 text-sm text-parchment-muted">
              {mode === "sign-in"
                ? t("login.welcomeBack")
                : t("login.setupBlurb")}
            </p>
          </div>

          <form className="grid gap-4" onSubmit={onSubmit}>
            {mode === "register" && (
              <label className="grid gap-2">
                <span className="label">{t("login.displayName")}</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  autoComplete="name"
                  placeholder={t("login.namePlaceholder")}
                />
              </label>
            )}
            <label className="grid gap-2">
              <span className="label">{t("login.email")}</span>
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder={t("login.emailPlaceholder")}
              />
            </label>
            <label className="grid gap-2">
              <span className="label">{t("login.password")}</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder={t("login.passwordPlaceholder")}
              />
            </label>

            {error && (
              <p className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className="btn-gold w-full">
              <LogIn className="w-4 h-4" />
              {busy
                ? mode === "sign-in"
                  ? t("login.signingIn")
                  : t("login.creating")
                : mode === "sign-in"
                  ? t("login.signIn")
                  : t("login.createAccount")}
            </button>
          </form>

          <div className="my-5 text-center text-[10px] font-semibold uppercase tracking-widest text-parchment-muted/60">
            {t("login.orPasskey")}
          </div>
          <button
            type="button"
            onClick={() => void onPasskey()}
            disabled={!passkeyOk || passkeyBusy}
            title={
              passkeyOk
                ? t("login.passkeyTitleOk")
                : t("login.passkeyTitleNo")
            }
            className="btn-ghost w-full disabled:opacity-50"
          >
            <Fingerprint className="w-4 h-4" />
            {passkeyBusy ? t("login.passkeyWaiting") : t("login.passkeyButton")}
          </button>
          <p className="mt-2 text-[11px] text-parchment-muted text-center">
            {passkeyOk
              ? t("login.passkeyHintOk")
              : t("login.passkeyHintNo")}
          </p>

          {registrationEnabled && (
            <p className="mt-6 text-center text-sm text-parchment-muted">
              {mode === "sign-in" ? (
                <>
                  {t("login.noAccount")}{" "}
                  <button
                    type="button"
                    onClick={() => setMode("register")}
                    className="text-accent hover:brightness-125 font-semibold"
                  >
                    {t("login.createOne")}
                  </button>
                </>
              ) : (
                <>
                  {t("login.haveAccount")}{" "}
                  <button
                    type="button"
                    onClick={() => setMode("sign-in")}
                    className="text-accent hover:brightness-125 font-semibold"
                  >
                    {t("login.signIn")}
                  </button>
                </>
              )}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
